import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { ChevronUp, ChevronDown, Pencil, Trash2, Plus } from 'lucide-react';
import { getCourseModules, createModule, updateModule, deleteModule } from '../../api/modules';
import { getLessons } from '../../api/lessons';
import { unwrapList } from '../../utils/api';
import { formatNumber } from '../../utils/datetime';
import LoadingState from '../../components/LoadingState/LoadingState';
import PageEmptyState from '../../components/PageEmptyState/PageEmptyState';
import TableCard from '../../components/TableCard/TableCard';
import { useConfirmDialog } from '../../components/ConfirmDialog/ConfirmDialog';
import { useI18n } from '../../i18n/I18nContext';
import { pluralSuffix } from '../teacher/pluralKey';
import './CourseModules.css';

// Lessons per module: the module row's own count when the API sends one, else counted from the
// course's lessons.
function withLessonCounts(moduleList, lessonsResult) {
  const lessons = Array.isArray(lessonsResult) ? lessonsResult : (lessonsResult?.data ?? []);
  const countByModule = {};
  lessons.forEach((l) => {
    if (l.moduleId) countByModule[l.moduleId] = (countByModule[l.moduleId] || 0) + 1;
  });
  return moduleList.map((m) => ({
    ...m,
    lessonCount: m.lessonCount ?? m._count?.lessons ?? countByModule[m.id] ?? 0,
  }));
}

// Uzbek never inflects the noun, English only has "1 module", Russian has three forms (one, few, many):
// the suffix picks the key of the count's grammatical form ('' = the general one).
const countForm = (lang, count) => {
  const form = pluralSuffix(lang, count);
  return form === 'One' || form === 'Few' ? form : '';
};

function CourseModules({ courseId: courseIdProp } = {}) {
  const { t, lang } = useI18n();
  const params = useParams();
  const courseId = courseIdProp || params.courseId || params.id;
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);
  // The first load failed: 'forbidden' | 'failed'. Save, delete and reorder failures use `error`.
  const [loadFailure, setLoadFailure] = useState(null);
  const [error, setError] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: ''
  });
  const [saving, setSaving] = useState(false);
  // The delete question: `confirm(options)` resolves true/false, `confirmDialog` is rendered once below.
  const [confirm, confirmDialog] = useConfirmDialog();
  const [titleTouched, setTitleTouched] = useState(false);
  // The backend's CreateModuleDto wants at least 3 characters; say so here rather than its generic 400.
  const titleProblem = !formData.title.trim()
    ? t('courseContent.modules.titleRequired')
    : formData.title.trim().length < 3 ? t('lessonAuthoring.module.tooShort') : null;
  const titleError = titleTouched ? titleProblem : null;

  const titleInputRef = useRef(null);

  // Reorder debounce and request deduplication
  const reorderDebounceRef = useRef(null);
  const reorderInProgressRef = useRef(false);
  // Only the newest load may write state: a slow earlier response must not overwrite a newer list.
  const loadSeqRef = useRef(0);

  // `silent` reloads (after a save, delete or failed reorder) keep the page on screen instead of
  // swapping the whole table for a skeleton and losing the scroll position.
  const loadModules = useCallback(async ({ silent = false } = {}) => {
    const seq = loadSeqRef.current + 1;
    loadSeqRef.current = seq;
    if (!silent) setLoading(true);
    try {
      const [modulesResult, lessonsResult] = await Promise.all([
        getCourseModules(courseId),
        getLessons(courseId).catch(() => []),
      ]);
      if (seq !== loadSeqRef.current) return;
      setModules(withLessonCounts(unwrapList(modulesResult), lessonsResult));
      setLoadFailure(null);
      setError(null);
    } catch (err) {
      if (seq !== loadSeqRef.current) return;
      const forbidden = err?.code === 403;
      if (silent) {
        // The table is still there: say what went wrong above it.
        setError(forbidden ? t('courseContent.modules.accessDenied') : t('courseContent.modules.loadError'));
      } else {
        setLoadFailure(forbidden ? 'forbidden' : 'failed');
      }
    } finally {
      if (seq === loadSeqRef.current) setLoading(false);
    }
  }, [courseId, t]);

  useEffect(() => {
    loadModules();
    return () => { loadSeqRef.current += 1; };
  }, [loadModules]);

  // SECURITY: Cleanup debounce timers on unmount
  useEffect(() => {
    return () => {
      if (reorderDebounceRef.current) {
        clearTimeout(reorderDebounceRef.current);
        reorderDebounceRef.current = null;
      }
    };
  }, []);

  // Opening the form (or switching it to another module) puts the cursor in the title field. The
  // browser scrolls a focused field into view, so on a phone the form is never left off-screen.
  useEffect(() => {
    if (showForm && titleInputRef.current) titleInputRef.current.focus();
  }, [showForm, editingId]);

  const handleAddClick = () => {
    setEditingId(null);
    setFormData({ title: '', description: '' });
    setTitleTouched(false);
    setShowForm(true);
  };

  const handleEditClick = (module) => {
    setEditingId(module.id);
    setFormData({
      title: module.title,
      description: module.description || ''
    });
    setTitleTouched(false);
    setShowForm(true);
  };

  const handleFormChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (titleProblem) {
      setTitleTouched(true);
      return;
    }

    setSaving(true);
    setError(null);

    try {
      if (editingId) {
        await updateModule(courseId, editingId, formData);
      } else {
        // Compute the next orderIndex from a fresh fetch right before creating, rather
        // than the possibly-stale `modules` in state (from a slow initial load, or another
        // tab/reviewer having created a module since this page loaded). Using max+1 instead
        // of list length also survives existing gaps/duplicates in orderIndex instead of
        // colliding with them. This narrows the race but can't fully close it without a
        // server-side atomic assignment (the module orderIndex column has no such
        // guarantee today) — a genuinely simultaneous create from two tabs can still
        // collide.
        const freshModules = await getCourseModules(courseId).catch(() => modules);
        const freshList = unwrapList(freshModules);
        const nextOrderIndex = freshList.reduce(
          (max, m) => Number.isFinite(Number(m.orderIndex)) ? Math.max(max, Number(m.orderIndex) + 1) : max,
          0
        );
        await createModule(courseId, {
          ...formData,
          orderIndex: nextOrderIndex
        });
      }
      await loadModules({ silent: true });
      setShowForm(false);
      setFormData({ title: '', description: '' });
    } catch (err) {
      // The page's own words, never the server's (often English) text.
      setError(t('courseContent.modules.saveError'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = useCallback(async (moduleId) => {
    const confirmed = await confirm({
      title: t('courseContent.modules.confirmDeleteTitle'),
      description: t('courseContent.modules.confirmDeleteHint'),
      confirmLabel: t('courseContent.modules.delete'),
      tone: 'danger',
    });
    if (!confirmed) return;

    setSaving(true);
    setError(null);

    try {
      await deleteModule(courseId, moduleId);
      await loadModules({ silent: true });
    } catch (err) {
      setError(t('courseContent.modules.deleteError'));
    } finally {
      setSaving(false);
    }
  }, [confirm, courseId, loadModules, t]);

  // Swap a module with its neighbour. The list updates at once; the new order is saved after a
  // short pause so a run of clicks becomes one save, and it rolls back if the save fails.
  const moveModule = useCallback((index, delta) => {
    const target = index + delta;
    if (target < 0 || target >= modules.length || reorderInProgressRef.current) return;

    const reordered = [...modules];
    [reordered[index], reordered[target]] = [reordered[target], reordered[index]];

    if (reorderDebounceRef.current) {
      clearTimeout(reorderDebounceRef.current);
    }

    setModules(reordered);

    reorderDebounceRef.current = setTimeout(async () => {
      if (reorderInProgressRef.current) return;
      reorderInProgressRef.current = true;

      try {
        await Promise.all(reordered.map((m, i) => updateModule(courseId, m.id, { orderIndex: i })));
        setError(null);
      } catch (err) {
        setModules(modules);
        setError(t('courseContent.modules.reorderError'));
      } finally {
        reorderInProgressRef.current = false;
      }
    }, 300);
  }, [modules, courseId, t]);

  if (loading) {
    return (
      <div className="course-modules">
        <LoadingState skeleton="table" rows={5} message={t('courseContent.modules.loadingMessage')} />
      </div>
    );
  }

  if (loadFailure) {
    return (
      <div className="course-modules">
        {loadFailure === 'forbidden' ? (
          <PageEmptyState variant="forbidden" size="sm" title={t('courseContent.modules.accessDenied')} />
        ) : (
          <PageEmptyState variant="error" size="sm" onRetry={() => loadModules()} />
        )}
      </div>
    );
  }

  return (
    <div className="course-modules">
      <div className="mod-toolbar">
        <div className="mod-toolbar-text">
          <div className="mod-heading-row">
            <h2 className="mod-heading">{t('courseContent.modules.pageTitle')}</h2>
            <span className="ds-badge ds-badge-neutral">
              {t(`courseContent.modules.moduleCount${countForm(lang, modules.length)}`, { count: modules.length })}
            </span>
          </div>
          <p className="mod-sub">{t('courseContent.modules.pageSubtitle')}</p>
        </div>
        {/* With no modules the empty state below carries the one yellow "add" button. */}
        {modules.length > 0 && (
          <button type="button" onClick={handleAddClick} className="ds-btn ds-btn-primary mod-add-btn">
            <Plus aria-hidden="true" />
            {t('courseContent.modules.addModule')}
          </button>
        )}
      </div>

      {error && (
        <div className="ui-alert" role="alert">
          <div>{error}</div>
        </div>
      )}

      {showForm && (
        <section className="section-card mod-form-card" aria-labelledby="mod-form-title">
          <div className="section-head">
            <h3 className="section-head__title" id="mod-form-title">
              {editingId ? t('courseContent.modules.editModule') : t('courseContent.modules.newModule')}
            </h3>
          </div>
          <form onSubmit={handleSubmit} className="mod-form">
            <div className="ui-field mod-field">
              <label className="ui-label" htmlFor="mod-title-input">
                {t('courseContent.modules.titleLabel')}
                <span className="ui-req" aria-hidden="true">*</span>
              </label>
              <input
                id="mod-title-input"
                ref={titleInputRef}
                type="text"
                name="title"
                value={formData.title}
                onChange={handleFormChange}
                onBlur={() => setTitleTouched(true)}
                aria-invalid={titleError ? 'true' : undefined}
                aria-required="true"
                aria-describedby="mod-title-msg"
                maxLength="100"
                placeholder={t('courseContent.modules.titlePlaceholder')}
              />
              {/* The message slot is always rendered at a fixed height: an error row that appears
                  on blur would shift the buttons below it between mousedown and mouseup on
                  Save/Cancel and the browser would swallow that first click. */}
              <div className="mod-msg" id="mod-title-msg">
                {titleError && <p className="ui-error" role="alert">{titleError}</p>}
              </div>
            </div>
            <div className="ui-field mod-field">
              <label className="ui-label" htmlFor="mod-desc-input">{t('courseContent.modules.descriptionLabel')}</label>
              <textarea
                id="mod-desc-input"
                name="description"
                value={formData.description}
                onChange={handleFormChange}
                maxLength="500"
                rows="3"
                placeholder={t('courseContent.modules.descriptionPlaceholder')}
              />
            </div>
            <div className="mod-form-actions">
              <button
                type="button"
                onClick={() => setShowForm(false)}
                className="ds-btn ds-btn-secondary"
              >
                {t('courseContent.modules.cancel')}
              </button>
              <button
                type="submit"
                disabled={saving || !formData.title.trim()}
                className="ds-btn ds-btn-primary"
              >
                {saving ? t('courseContent.modules.saving') : t('courseContent.modules.save')}
              </button>
            </div>
          </form>
        </section>
      )}

      {modules.length === 0 ? (
        <PageEmptyState
          variant="docs"
          title={t('courseContent.modules.emptyTitle')}
          description={t('courseContent.modules.emptyDescription')}
          actionLabel={t('courseContent.modules.emptyAction')}
          onAction={handleAddClick}
        />
      ) : (
        <TableCard className="mod-card" aria-label={t('courseContent.modules.pageTitle')}>
          <table className="modules-table ui-table">
            <thead>
              <tr>
                <th className="mod-col-num">{t('courseContent.modules.colNumber')}</th>
                <th>{t('courseContent.modules.colModule')}</th>
                <th className="mod-col-lessons">{t('courseContent.modules.colLessons')}</th>
                <th className="mod-col-order">{t('courseContent.modules.colOrder')}</th>
                <th className="mod-col-actions">{t('courseContent.modules.colActions')}</th>
              </tr>
            </thead>
            <tbody>
              {modules.map((module, index) => (
                <tr key={module.id}>
                  <td className="mod-col-num mod-cell-num">{index + 1}</td>
                  <td className="mod-cell-main">
                    <span className="mod-title">{module.title}</span>
                    {module.description && <span className="mod-desc">{module.description}</span>}
                  </td>
                  <td className="mod-col-lessons mod-cell-count" data-label={t('courseContent.modules.colLessons')}>
                    {formatNumber(module.lessonCount ?? module._count?.lessons ?? 0)}
                  </td>
                  <td className="mod-col-order mod-cell-order">
                    <div className="ui-row-actions">
                      <button
                        type="button"
                        onClick={() => moveModule(index, -1)}
                        disabled={index === 0}
                        className="ui-icon-btn"
                        title={t('courseContent.modules.moveUp')}
                        aria-label={t('courseContent.modules.moveUp')}
                      >
                        <ChevronUp aria-hidden="true" />
                      </button>
                      <button
                        type="button"
                        onClick={() => moveModule(index, 1)}
                        disabled={index === modules.length - 1}
                        className="ui-icon-btn"
                        title={t('courseContent.modules.moveDown')}
                        aria-label={t('courseContent.modules.moveDown')}
                      >
                        <ChevronDown aria-hidden="true" />
                      </button>
                    </div>
                  </td>
                  <td className="mod-col-actions mod-cell-actions">
                    <div className="ui-row-actions">
                      <button
                        type="button"
                        onClick={() => handleEditClick(module)}
                        className="ui-icon-btn"
                        title={t('courseContent.modules.edit')}
                        aria-label={t('courseContent.modules.edit')}
                      >
                        <Pencil aria-hidden="true" />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDelete(module.id)}
                        className="ui-icon-btn ui-icon-btn--danger"
                        title={t('courseContent.modules.delete')}
                        aria-label={t('courseContent.modules.delete')}
                      >
                        <Trash2 aria-hidden="true" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </TableCard>
      )}

      {confirmDialog}
    </div>
  );
}

export default CourseModules;
